// One "Add X" control, used wherever a page can attach a record.
//
// The first version of this was create-only: a text link that turned into a name
// field. Two things were wrong with it. It did not look like a control, so it read
// as a caption rather than something to press. And it could only ever make a NEW
// record — so attaching a project that already existed still meant leaving the page,
// finding it, and linking from the other side, which is the four-step round trip
// this was supposed to remove.
//
// So: search first, create second. The list is what already exists, and creating is
// one more row at the bottom of it, offered only when nothing matches exactly. That
// is also the order of the underlying mistake being prevented — a duplicate record
// is much more expensive than a missing one.
//
// The markup is RelationField's, lifted out so the aside and the section headings
// cannot drift apart.

import { useState } from 'react'

import { KINDS, titleField, type EntityKind } from '../lib/model'
import { entitiesOfKind } from '../lib/reducer'
import { useStore } from '../lib/store'
import { Button } from './common'
import { Icon, iconName } from './Icon'

export function AddRecord({
  kind,
  label,
  exclude = [],
  onPick,
  onCreate,
}: {
  kind: EntityKind
  /** button text; defaults to "Add <kind>" */
  label?: string | undefined
  /** ids already attached here, kept out of the list */
  exclude?: string[] | undefined
  onPick: (id: string) => void
  onCreate: (name: string) => void
}) {
  const { state } = useStore()
  const [open, setOpen] = useState(false)
  const [q, setQ] = useState('')
  const def = KINDS[kind]
  const tkey = titleField(kind)

  const ql = q.trim().toLowerCase()
  const nameOf = (id: string) => String(state.entities[id]?.fields[tkey] ?? '')
  const skip = new Set(exclude)

  const all = entitiesOfKind(state, kind).filter((e) => !e.archived && !skip.has(e.id))
  // An exact match means "you already have this one", so the Create row would be
  // offering a duplicate. Checked against everything, not just the visible slice.
  const exact = all.some((e) => String(e.fields[tkey] ?? '').toLowerCase() === ql)
  const candidates = all
    .filter(
      (e) =>
        ql === '' ||
        String(e.fields[tkey] ?? '')
          .toLowerCase()
          .includes(ql),
    )
    .slice(0, 6)

  const reset = () => {
    setQ('')
    setOpen(false)
  }
  const pick = (id: string) => {
    onPick(id)
    reset()
  }
  const create = () => {
    const name = q.trim()
    if (!name) return
    onCreate(name)
    reset()
  }

  if (!open) {
    return (
      <Button size="dense" onClick={() => setOpen(true)}>
        <Icon name="Plus" size={14} /> {label ?? `Add ${def.singular.toLowerCase()}`}
      </Button>
    )
  }

  return (
    <div className="relative">
      <input
        autoFocus
        value={q}
        onChange={(e) => setQ(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Escape') reset()
          if (e.key === 'Enter') {
            // Enter takes the top match, because picking the thing you already have
            // is the common case. Only when nothing matches does it create.
            if (candidates[0]) pick(candidates[0].id)
            else if (q.trim() && !exact) create()
          }
        }}
        // Delayed, so a click on a row in the list below lands before the blur
        // tears it down.
        onBlur={() => setTimeout(reset, 150)}
        placeholder={`Find or create a ${def.singular.toLowerCase()}…`}
        className="rounded-control text-body w-64 border px-2.5 py-1.5 outline-none"
        style={{ borderColor: 'var(--border)', background: 'var(--panel)' }}
      />
      {(candidates.length > 0 || (q.trim() && !exact)) && (
        <ul
          className="rounded-control absolute top-full right-0 z-40 mt-1 w-64 overflow-hidden border py-1 shadow-lg"
          style={{ background: 'var(--panel)', borderColor: 'var(--border)' }}
        >
          {candidates.map((e) => (
            <li key={e.id}>
              <button
                type="button"
                onMouseDown={(ev) => {
                  ev.preventDefault()
                  pick(e.id)
                }}
                className="text-body flex w-full items-center gap-2 px-2.5 py-1.5 text-left hover:bg-[var(--hover)]"
              >
                <Icon name={iconName(def.icon)} size={14} style={{ color: 'var(--muted)' }} />
                <span className="truncate">{nameOf(e.id) || 'Untitled'}</span>
              </button>
            </li>
          ))}
          {q.trim() && !exact && (
            <li>
              <button
                type="button"
                onMouseDown={(ev) => {
                  ev.preventDefault()
                  create()
                }}
                className="text-body flex w-full items-center gap-2 px-2.5 py-1.5 text-left hover:bg-[var(--hover)]"
                style={{ color: 'var(--accent)' }}
              >
                <Icon name="Plus" size={14} />
                <span className="truncate">
                  Create {def.singular.toLowerCase()}: “{q.trim()}”
                </span>
              </button>
            </li>
          )}
        </ul>
      )}
    </div>
  )
}
